import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from 'src/i18n';

import '../../library/editors/elements/d-select-tag';
import '../../library/editors/elements/d-checkbox';
import '../../library/fields/d-view-text';
import '../../library/fields/d-expansion';
import '../../library/elements/d-action';
import '../../library/elements/d-wrap';
import type { SelectTagOption } from '../../library/editors/elements/select-tag-option';
import { joinWithAnd } from 'src/utilities/text';

import type { EmergencyAccessSaveDetail } from './emergency-access-save-detail';

@customElement('d-emergency-access')
export class DEmergencyAccess extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
      padding: 12px 0 10px 0;
    }
    d-checkbox {
      margin-top: 12px;
    }
  `;

  @property({ type: Object })
  fieldsByCode = {};
  @property({ type: Boolean })
  edit = false;
  @property({ type: Array })
  editValue: string[] = [];
  @property({ type: Boolean })
  currentUserHasAccess = false;
  @property({ type: Array })
  people: SelectTagOption[] = [];
  @property({ type: Array })
  accessList: string[] = [];

  /** Sant mens en lagring er underveis. */
  @state()
  private saving = false;

  private onEdit() {
    this.editValue = [...this.accessList];
    this.edit = true;
  }

  private onSave() {
    // Redigeringen blir stående åpen til lagringen er ferdig, så uten denne
    // sperren ville et dobbeltklikk sende to samtidige lagringer.
    if (this.saving) {
      return;
    }
    this.saving = true;
    // Ikke skriv editValue til accessList her: da ville lista vise den nye
    // tilgangen selv om lagringen feilet. accessList kommer tilbake fra
    // view-modellen når lagringen faktisk har gått gjennom.
    this.dispatchEvent(
      new CustomEvent<EmergencyAccessSaveDetail>('save-emergency-access', {
        composed: true,
        bubbles: true,
        detail: {
          accessList: this.editValue,
          // Lukk redigeringen først når lagringen har gått gjennom. Feiler den,
          // beholder brukeren utvalget sitt og kan prøve igjen med én gang.
          callback: (saved) => {
            this.saving = false;
            if (saved) {
              this.edit = false;
            }
          },
        },
      }),
    );
  }

  private onValueChanged(e: CustomEvent<{ value: string[] }>) {
    e.stopPropagation();
    this.editValue = [...e.detail.value];
  }

  private get accessors() {
    if (!this.edit) {
      return joinWithAnd(this.people.filter((p) => this.accessList.includes(p.value)).map((p) => p.text));
    }
    return '';
  }

  render() {
    return html`
      <div>
        <d-wrap split-reverse baseline>
          ${this.currentUserHasAccess
            ? html`<d-action>
                ${this.edit
                  ? html`<span @click=${this.onSave}>${t('content.done')}</span>`
                  : html`<span @click=${this.onEdit}>${t('content.edit')}</span>`}
              </d-action>`
            : nothing}
          <d-view-text wrap-label field="organization_emergencyAccess" value="${this.accessors}"></d-view-text>
        </d-wrap>
        <d-expansion ?opened=${this.edit}>
          <d-select-tag
            .options=${this.people}
            type="tag"
            .value=${this.editValue}
            ?disabled=${this.saving}
            @value-changed=${this.onValueChanged}
          >
          </d-select-tag>
        </d-expansion>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-emergency-access': DEmergencyAccess;
  }
}
